import { LegalLinks } from "./legal/LegalLinks";
import { useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { LangPicker } from "../components/LangPicker";
import { Button, Field, Notice, PasswordInput, errorText } from "../components/ui";
import { Link, useRouter } from "../lib/router";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "../components/Logo";

export type AuthMode = "login" | "register" | "forgot" | "reset";

export default function AuthPage({ mode }: { mode: AuthMode }) {
  const { t, lang, pref, setPref } = useI18n();
  const { refresh } = useAuth();
  const { navigate, route } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  /** "Stay signed in for 30 days" — on by default; off on a shared computer. */
  const [remember, setRemember] = useState(true);
  /** Forgot password: the e-mail went out (or mail isn't set up yet). */
  const [forgotDone, setForgotDone] = useState<null | "sent" | "unavailable">(null);
  const newPassword = mode === "register" || mode === "reset";
  const mismatch = newPassword && confirm !== "" && confirm !== password;
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword && password.length < 8) return setError(t("auth.passwordTooShort"));
    if (newPassword && password !== confirm) return setError(t("auth.passwordMismatch"));
    setBusy(true);
    try {
      if (mode === "forgot") {
        const r = await api.post<{ sent: boolean }>("/api/auth/forgot", { email, language: pref });
        setForgotDone(r.sent ? "sent" : "unavailable");
        return;
      }
      if (mode === "register") await api.post("/api/auth/register", { email, password, displayName: name, language: pref });
      else if (mode === "reset") await api.post("/api/auth/reset", { token: route.query.get("token") ?? "", password });
      else await api.post("/api/auth/login", { email, password, remember });
      try {
        localStorage.setItem("nutricart.hasAccount", "1");
      } catch {}
      await refresh();
      // Came from a family's shopping-list link: back there, where joining happens.
      let list: string | null = null;
      try {
        list = sessionStorage.getItem("nutricart.joinList");
      } catch {}
      navigate(list ? `/list/${list}` : "/", { replace: true });
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <div className="auth-card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <Link to="/" className="brand" aria-label="NutriCart">
            <ArrowLeft size={20} />
            <span className="brand-mark">
              <LogoMark size={26} />
            </span>
            NutriCart
          </Link>
          <LangPicker value={lang} onChange={(v) => setPref(v as any)} compact />
        </div>
        {(mode === "login" || mode === "register") && (
          <div className="segmented auth-switch" role="group">
            <Link to="/register" replace aria-pressed={mode === "register"} onClick={() => setError(null)}>
              {t("auth.title.register")}
            </Link>
            <Link to="/login" replace aria-pressed={mode === "login"} onClick={() => setError(null)}>
              {t("auth.title.login")}
            </Link>
          </div>
        )}
        <div className="stack" style={{ gap: 6 }}>
          <h1>{t(`auth.title.${mode}`)}</h1>
          <p className="muted">{mode === "forgot" ? t("auth.forgotIntro") : mode === "reset" ? t("auth.resetIntro") : t("auth.tagline")}</p>
        </div>
        {forgotDone ? (
          <div className="card stack">
            <Notice tone={forgotDone === "sent" ? "good" : "warn"}>{t(forgotDone === "sent" ? "auth.forgotSent" : "auth.forgotUnavailable", { email })}</Notice>
            <Link to="/login" replace className="btn block">
              {t("auth.backToLogin")}
            </Link>
          </div>
        ) : (
          <form className="form card" onSubmit={submit} noValidate>
            {mode === "register" && (
              <Field label={t("auth.name")} htmlFor="auth-name">
                <input id="auth-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoComplete="nickname" />
              </Field>
            )}
            {mode !== "reset" && (
              <Field label={t("auth.email")} htmlFor="auth-email">
                <input id="auth-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" />
              </Field>
            )}
            {mode !== "forgot" && (
              <Field label={t(mode === "reset" ? "auth.newPassword" : "auth.password")} htmlFor="auth-password" hint={newPassword ? t("auth.passwordHint") : undefined}>
                <PasswordInput id="auth-password" value={password} onChange={setPassword} autoComplete={newPassword ? "new-password" : "current-password"} />
              </Field>
            )}
            {newPassword && (
              <Field label={t("auth.confirmPassword")} htmlFor="auth-confirm" error={mismatch ? t("auth.passwordMismatch") : null}>
                <PasswordInput id="auth-confirm" value={confirm} onChange={setConfirm} autoComplete="new-password" invalid={mismatch} />
              </Field>
            )}
            {mode === "login" && (
              <label className="auth-remember">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                <span>
                  {t("auth.remember")}
                  <span className="small muted" style={{ display: "block" }}>
                    {t("auth.rememberSub")}
                  </span>
                </span>
              </label>
            )}
            {mode === "login" && (
              <Link to="/forgot" className="small" style={{ alignSelf: "flex-end" }} onClick={() => setError(null)}>
                {t("auth.forgot")}
              </Link>
            )}
            {error && <Notice tone="bad">{error}</Notice>}
            <Button type="submit" className="block" busy={busy}>
              {t(`auth.submit.${mode}`)}
            </Button>
            {mode === "register" && <p className="small muted">{t("auth.terms")}</p>}
            {mode === "register" && <LegalLinks />}
          </form>
        )}
        <p className="muted" style={{ textAlign: "center" }}>
          {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
          <Link
            to={mode === "login" ? "/register" : "/login"}
            replace
            className="btn ghost"
            onClick={() => setError(null)}
          >
            {mode === "login" ? t("auth.createAccount") : t("auth.login")}
          </Link>
        </p>
      </div>
    </main>
  );
}
